import { getEnvironment } from 'utils/isomorphicEnvironment'

type JSError = { message?: string; code?: string; stack?: string }
type LogError = { message: string; code?: string; stack?: string }

const LOG_LEVELS = ['debug', 'info', 'notice', 'warning', 'error', 'critical', 'log'] as const
type LogLevel = typeof LOG_LEVELS[number]

/**
 * @description Convert JSError type into LogError type
 */
const formatJSError = (error: JSError) => ({
  message: error.message || error.code || 'Error',
  code: error.code,
  stack: error.stack,
})

/**
 * @description Check if the error is a JSError or JSError-like
 */
const isJSError = (e: unknown): e is JSError =>
  e && typeof e === 'object' && (e as any).stack && ((e as any).message || (e as any).code)

/**
 * @description Format different kinds of values into LogError[] or undefined
 */
const formatErrors = (
  errors: string | JSError | JSError[] | unknown, // unknown here allows passing formatted errors
  log: { errors?: LogError[] },
): LogError[] | undefined => {
  if (errors !== undefined) {
    if (typeof errors === 'string') {
      return [{ message: errors }]
    } else if (isJSError(errors) && Array.isArray(log.errors)) {
      return [...log.errors, formatJSError(errors)]
    } else if (Array.isArray(errors)) {
      return errors.map((error) => (isJSError(error) ? formatJSError(error) : error))
    }
  }

  return undefined
}

type LogRecord = {
  message?: string
  status?: string
  stack?: string
  elapsedTime?: number
  errors?: LogError[]
  requestUrl?: string
  uuid?: string
  extra?: Record<string, unknown>
  headers?: Record<string, string>
  source?: string
  requestType?: string
  actor?: string
  involves?: string
}

/**
 * @description Format values provided into a log object
 */
const formatLogs = (args: LogRecord & { level: LogLevel }) => {
  const {
    message,
    status,
    elapsedTime,
    errors,
    level,
    requestUrl,
    uuid,
    extra,
    headers,
    source,
    requestType,
    actor,
    involves,
  } = args

  let log: {
    message?: string
    errors?: LogError[]
    log_level?: string
    service?: 'webclient'
    elapsedTime?: string
    status?: string
    requestUrl?: string
    'gousto-request-id'?: string
    extra?: Record<string, unknown>
    headers?: Record<string, string>
    source?: string
    request_type?: string
    actor?: string
    involves?: string
    timestamp?: false | Date
  } = {
    log_level: level.toUpperCase(),
    service: 'webclient',
  }

  if (isJSError(args)) {
    log.message = 'Error'
    log.errors = [formatJSError(args)]
  }

  if (typeof message === 'string') {
    log.message = message
  } else {
    // eslint-disable-next-line no-console
    console.error("'message' field not provided or not a string")
  }

  log = {
    ...log,
    elapsedTime: typeof elapsedTime === 'number' ? `${elapsedTime}ms` : elapsedTime,
    status,
    errors: formatErrors(errors, log),
    requestUrl,
    'gousto-request-id': uuid,
    extra,
    headers: headers && {
      ...headers,
      cookie: '',
    },
    source,
    request_type: requestType,
    actor,
    involves,
    timestamp: getEnvironment() !== 'production' && new Date(),
  }

  Object.keys(log).forEach((key) => {
    const typedKey = key as keyof typeof log
    if (!log[typedKey]) {
      delete log[typedKey]
    }
  })

  return log
}

/**
 * @description Log to console object if logging is enabled
 */
const logToConsole = (
  args: string | LogRecord | Error,
  level: LogLevel,
  consoleEnabled: boolean,
) => {
  let log
  if (typeof args === 'string') {
    log = { message: args, level }
  } else if (args instanceof Error) {
    log = { message: args.message, stack: args.stack, level }
  } else {
    log = { ...args, level }
  }
  const logs = JSON.stringify(formatLogs(log))

  if (consoleEnabled) {
    // eslint-disable-next-line no-console
    console.log(logs)

    return null
  } else {
    return logs
  }
}

/**
 * @description Create a logger instance using functions above
 */
const getBrowserLogger = (isConsoleEnabled: () => boolean) =>
  LOG_LEVELS.reduce(
    (reduced, level) => ({
      ...reduced,
      [level]: (args: string | LogRecord | Error) => logToConsole(args, level, isConsoleEnabled()),
    }),
    {} as Record<LogLevel, (args: string | object) => void>,
  )

/**
 * @description Control which environments have this logger enabled
 */
const isLoggingEnabledForEnvironment = () => getEnvironment() === 'local'

/**
 * Warning! This won't log to Datadog!
 * It only provides local development logs
 */
// eslint-disable-next-line import/no-default-export
export default getBrowserLogger(isLoggingEnabledForEnvironment)
